import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  useGetChecklistTemplateQuery,
  useCreateChecklistTemplateMutation,
  useUpdateChecklistTemplateMutation,
  useAddTemplateItemsMutation,
  useUpdateTemplateItemMutation,
  useDeleteTemplateItemMutation,
  useImportTemplateItemsMutation,
  ChecklistTemplateItem
} from "../../utils/api/endpoints/checklists/checklistApi";
import usePageTitle from "../../utils/hooks/usePageTitle";
import toast from "react-hot-toast";
import { FiSearch, FiPlus, FiLayers, FiTarget, FiZap, FiInfo, FiCheck, FiX } from "react-icons/fi";

const TARGET_TYPES = [
  "Web", "iOS", "Android", "API", "Web3", "Source Code",
  "Protocol", "Database", "Network/Infrastructure", "Smart Contract", "Other"
];

const ASVS_LEVELS = ["L1", "L2", "L3"];

interface ChecklistItemForm extends Partial<ChecklistTemplateItem> {
  tempId?: string;
}

const ChecklistTemplateEditor: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const isViewMode = location.pathname.includes("/view");
  const isEditMode = !!id && !isViewMode;

  usePageTitle(isViewMode ? "View Template" : isEditMode ? "Edit Template" : "Create Template");

  const { data: templateData, isLoading } = useGetChecklistTemplateQuery(
    parseInt(id!),
    { skip: !isEditMode && !isViewMode }
  );

  const [createTemplate, { isLoading: isCreating }] = useCreateChecklistTemplateMutation();
  const [updateTemplate, { isLoading: isUpdating }] = useUpdateChecklistTemplateMutation();
  const [addTemplateItems] = useAddTemplateItemsMutation();
  const [updateTemplateItem] = useUpdateTemplateItemMutation();
  const [deleteTemplateItem] = useDeleteTemplateItemMutation();
  const [importTemplateItems, { isLoading: isImporting }] = useImportTemplateItemsMutation();

  const [templateName, setTemplateName] = useState("");
  const [description, setDescription] = useState("");
  const [version, setVersion] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [framework, setFramework] = useState("CUSTOM");
  const [selectedTargetTypes, setSelectedTargetTypes] = useState<string[]>([]);
  const [items, setItems] = useState<ChecklistItemForm[]>([]);
  const [deletedItemIds, setDeletedItemIds] = useState<number[]>([]); // Track deleted items

  // Import functionality state
  const [showImportModal, setShowImportModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [importErrors, setImportErrors] = useState<string[]>([]);
  const [itemSearchQuery, setItemSearchQuery] = useState("");
  const [filterCategory, setFilterCategory] = useState("All");
  const [filterAsvsLevel, setFilterAsvsLevel] = useState("All"); // All, L1, L2, L3
  const [filterTarget, setFilterTarget] = useState("All"); // All, Web, API

  const uniqueCategories = useMemo(() => {
    const cats = new Set<string>();
    items.forEach(item => {
      if (item.category) cats.add(item.category);
    });
    return Array.from(cats).sort();
  }, [items]);

  const filteredItems = useMemo(() => {
    return items.filter(item => {
      // 1. Search Query
      if (itemSearchQuery.trim()) {
        const query = itemSearchQuery.toLowerCase();
        const matchesSearch = (
          item.title?.toLowerCase().includes(query) ||
          item.description?.toLowerCase().includes(query) ||
          item.category?.toLowerCase().includes(query) ||
          item.test_objective?.toLowerCase().includes(query)
        );
        if (!matchesSearch) return false;
      }

      // 2. Category Filter
      if (filterCategory !== "All" && item.category !== filterCategory) {
        return false;
      }

      // 3. Level Filter (ASVS only)
      if (filterAsvsLevel !== "All") {
        if (filterAsvsLevel === "L1" && !item.l1) return false;
        if (filterAsvsLevel === "L2" && !item.l2) return false;
        if (filterAsvsLevel === "L3" && !item.l3) return false;
      }

      // 4. Target Mode Filter
      if (filterTarget === "Web" && !item.is_web) return false;
      if (filterTarget === "API" && !item.is_api) return false;
      if (filterTarget === "Mobile" && !item.is_mobile) return false;
      if (filterTarget === "Infra" && !item.is_infra) return false;

      return true;
    });
  }, [items, itemSearchQuery, filterCategory, filterAsvsLevel, filterTarget]);

  useEffect(() => {
    if (templateData?.data) {
      const template = templateData.data;
      setTemplateName(template.template_name || "");
      setDescription(template.description || "");
      setVersion(template.version?.toString() || "");
      setIsActive(template.is_active ?? true);
      setFramework(template.framework || "CUSTOM");
      setSelectedTargetTypes(template.targetMappings?.map(m => m.target_type) || []);
      const templateItems = template.items || [];
      setItems(templateItems);
    }
  }, [templateData]);

  const handleTargetTypeToggle = (targetType: string) => {
    if (isViewMode) return;
    setSelectedTargetTypes(prev =>
      prev.includes(targetType) ? prev.filter(t => t !== targetType) : [...prev, targetType]
    );
  };

  const handleAddItem = () => {
    const newItem: ChecklistItemForm = {
      tempId: `temp-${Date.now()}`,
      item_order: items.length + 1,
      category: "",
      title: "",
      description: "",
      test_objective: "",
      expected_evidence: "",
      requirement_id: "",
      l1: false,
      l2: false,
      l3: false,
      is_web: false,
      is_api: false,
      is_mobile: false,
      is_infra: false,
      is_required: true
    };
    setItems([...items, newItem]);
  };

  const handleRemoveItem = (index: number) => {
    const itemToRemove = items[index];
    // If item has an item_id, it exists in the database and needs to be deleted
    if (itemToRemove.item_id) {
      setDeletedItemIds(prev => [...prev, itemToRemove.item_id!]);
    }
    setItems(items.filter((_, i) => i !== index));
  };

  const handleItemChange = (index: number, field: keyof ChecklistItemForm, value: any) => {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], [field]: value };
    setItems(newItems);
  };

  // Import functionality handlers
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setImportErrors([]);
    }
  };

  const handleImportSubmit = async () => {
    if (!selectedFile) return;

    try {
      let templateIdToUse: number;

      if (id) {
        // Edit mode: import into existing template
        templateIdToUse = parseInt(id);
      } else {
        // Create mode: create template first, then import
        if (!templateName.trim()) {
          toast.error("Template name is required before importing");
          return;
        }

        if (selectedTargetTypes.length === 0) {
          toast.error("Please select at least one target type before importing");
          return;
        }

        const payload = {
          template_name: templateName,
          description,
          version,
          is_active: isActive,
          framework,
          target_types: selectedTargetTypes,
          items: items.map((item, index) => ({
            item_order: index + 1,
            category: item.category || "",
            title: item.title || "",
            description: item.description || "",
            test_objective: item.test_objective || "",
            expected_evidence: item.expected_evidence || "",
            references: item.references || "",
            requirement_id: item.requirement_id || "",
            l1: item.l1 || false,
            l2: item.l2 || false,
            l3: item.l3 || false,
            is_web: item.is_web || false,
            is_api: item.is_api || false,
            is_mobile: item.is_mobile || false,
            is_infra: item.is_infra || false,
            is_required: item.is_required ?? true
          }))
        };

        const created = await createTemplate(payload).unwrap();
        templateIdToUse = created.data.template_id;
      }

      const result = await importTemplateItems({
        templateId: templateIdToUse,
        file: selectedFile
      }).unwrap();

      toast.success(`Successfully imported ${result.data.imported_count} test cases`);
      setShowImportModal(false);
      setSelectedFile(null);
      setImportErrors([]);

      if (id) {
        // Refresh the template data to show new items in edit mode
        window.location.reload();
      } else {
        // Navigate to edit page for the newly created template
        navigate(`/dashboard/checklist-templates/${templateIdToUse}/edit`);
      }
    } catch (err: any) {
      console.error("Import error:", err);
      if (err?.data?.errors && Array.isArray(err.data.errors)) {
        setImportErrors(err.data.errors);
      } else {
        toast.error(err?.data?.message || "Failed to import test cases");
      }
    }
  };

  const downloadSampleFile = (format: string) => {
    let content = "";
    let filename = "";
    let mimeType = "";

    if (format === 'json') {
      filename = 'checklist-template-sample.json';
    } else if (format === 'mobile-csv') {
      filename = 'mobile-checklist-sample.csv';
    } else if (format === 'csv') {
      filename = 'checklist-template-sample.csv';
    }

    if (filename) {
      // Static files
      const link = document.createElement('a');
      link.href = `/sample-files/${filename}`;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      return;
    }

    // Dynamic generation
    if (format === 'web-csv') {
      filename = 'web-only-sample.csv';
      mimeType = 'text/csv';
      content = 'title,category,description,test_objective,expected_evidence,references,requirement_id,l1,l2,l3,is_web,is_api,is_required,is_mobile,is_infra\n' +
        '"SQL Injection Prevention","Input Validation","Verify parameterized queries...","Ensure inputs are parameterized...","Code review showing parameters...","OWASP A03","A03",true,true,true,true,false,"true",false,false';
    } else if (format === 'web-json') {
      filename = 'web-only-sample.json';
      mimeType = 'application/json';
      content = JSON.stringify([{
        title: "SQL Injection Prevention",
        category: "Input Validation",
        description: "Verify parameterized queries...",
        test_objective: "Ensure inputs are parameterized...",
        expected_evidence: "Code review showing parameters...",
        references: "OWASP A03",
        requirement_id: "A03",
        l1: true,
        l2: true,
        l3: true,
        is_web: true,
        is_api: false,
        is_required: true,
        is_mobile: false,
        is_infra: false
      }], null, 2);
    } else if (format === 'api-csv') {
      filename = 'api-only-sample.csv';
      mimeType = 'text/csv';
      content = 'title,category,description,test_objective,expected_evidence,references,requirement_id,l1,l2,l3,is_web,is_api,is_required,is_mobile,is_infra\n' +
        '"API Rate Limiting","Rate Limiting","Verify rate limiting headers...","Ensure API returns 429...","HTTP response logs with 429...","OWASP API4","API4",true,true,true,false,true,"true",false,false';
    } else if (format === 'api-json') {
      filename = 'api-only-sample.json';
      mimeType = 'application/json';
      content = JSON.stringify([{
        title: "API Rate Limiting",
        category: "Rate Limiting",
        description: "Verify rate limiting headers...",
        test_objective: "Ensure API returns 429...",
        expected_evidence: "HTTP response logs with 429...",
        references: "OWASP API4",
        requirement_id: "API4",
        l1: true,
        l2: true,
        l3: true,
        is_web: false,
        is_api: true,
        is_required: true,
        is_mobile: false,
        is_infra: false
      }], null, 2);
    } else if (format === 'mobile-only-csv') {
      filename = 'mobile-only-sample.csv';
      mimeType = 'text/csv';
      content = 'title,category,description,test_objective,expected_evidence,references,requirement_id,is_web,is_api,is_required,is_mobile,is_infra\n' +
        '"Root and Jailbreak Detection Verification","Platform Security","Test the mobile application\'s resilience by verifying it correctly detects rooted (Android) or jailbroken (iOS) devices.","Ensure the application terminates or restricts operations on compromised environments.","Screenshots of root detection alerts.","OWASP MASVS-RESILIENCE-1","MASVS-v1.4:R1",false,false,"true",true,false';
    } else if (format === 'mobile-only-json') {
      filename = 'mobile-only-sample.json';
      mimeType = 'application/json';
      content = JSON.stringify([{
        title: "Root and Jailbreak Detection Verification",
        category: "Platform Security",
        description: "Test the mobile application's resilience by verifying it correctly detects rooted (Android) or jailbroken (iOS) devices.",
        test_objective: "Ensure the application terminates or restricts operations on compromised environments.",
        expected_evidence: "Screenshots of root detection alerts.",
        references: "OWASP MASVS-RESILIENCE-1",
        requirement_id: "MASVS-v1.4:R1",
        is_web: false,
        is_api: false,
        is_required: true,
        is_mobile: true,
        is_infra: false
      }], null, 2);
    } else if (format === 'infra-only-csv') {
      filename = 'infra-only-sample.csv';
      mimeType = 'text/csv';
      content = 'title,category,description,test_objective,expected_evidence,references,requirement_id,is_web,is_api,is_required,is_mobile,is_infra\n' +
        '"Secure SSH Port Configuration","Infrastructure Security","Verify SSH is configured to use key-based authentication only and password authentication is disabled.","Ensure unauthorized password bruteforcing is prevented.","SSH configuration showing PasswordAuthentication no.","CIS Benchmark 2.2.4","CIS-2.2.4",false,false,"true",false,true';
    } else if (format === 'infra-only-json') {
      filename = 'infra-only-sample.json';
      mimeType = 'application/json';
      content = JSON.stringify([{
        title: "Secure SSH Port Configuration",
        category: "Infrastructure Security",
        description: "Verify SSH is configured to use key-based authentication only and password authentication is disabled.",
        test_objective: "Ensure unauthorized password bruteforcing is prevented.",
        expected_evidence: "SSH configuration showing PasswordAuthentication no.",
        references: "CIS Benchmark 2.2.4",
        requirement_id: "CIS-2.2.4",
        is_web: false,
        is_api: false,
        is_required: true,
        is_mobile: false,
        is_infra: true
      }], null, 2);
    }

    if (content) {
      const blob = new Blob([content], { type: mimeType });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!templateName.trim()) {
      toast.error("Template name is required");
      return;
    }

    if (selectedTargetTypes.length === 0) {
      toast.error("Please select at least one target type");
      return;
    }

    try {
      if (isEditMode) {
        // Step 1: Update template metadata
        await updateTemplate({
          id: parseInt(id!),
          template_name: templateName,
          description,
          version,
          is_active: isActive,
          framework,
          target_types: selectedTargetTypes
        }).unwrap();

        // Step 2: Handle deleted items
        for (const itemId of deletedItemIds) {
          try {
            await deleteTemplateItem({
              templateId: parseInt(id!),
              itemId
            }).unwrap();
          } catch (err) {
            console.error(`Failed to delete item ${itemId}:`, err);
          }
        }

        // Step 3: Separate new items and existing items
        const newItems = items.filter(item => !item.item_id || item.tempId);
        const existingItems = items.filter(item => item.item_id && !item.tempId);

        // Step 4: Add new items
        if (newItems.length > 0) {
          await addTemplateItems({
            templateId: parseInt(id!),
            items: newItems.map((item, index) => ({
              item_order: items.indexOf(item) + 1,
              category: item.category || "",
              title: item.title || "",
              description: item.description || "",
              test_objective: item.test_objective || "",
              expected_evidence: item.expected_evidence || "",
              references: item.references || "",
              requirement_id: item.requirement_id || "",
              l1: item.l1 || false,
              l2: item.l2 || false,
              l3: item.l3 || false,
              is_web: item.is_web || false,
              is_api: item.is_api || false,
              is_mobile: item.is_mobile || false,
              is_infra: item.is_infra || false,
              is_required: item.is_required ?? true
            }))
          }).unwrap();
        }

        // Step 5: Update existing items
        for (const item of existingItems) {
          try {
            await updateTemplateItem({
              templateId: parseInt(id!),
              itemId: item.item_id!,
              data: {
                item_order: items.indexOf(item) + 1,
                category: item.category || "",
                title: item.title || "",
                description: item.description || "",
                test_objective: item.test_objective || "",
                expected_evidence: item.expected_evidence || "",
                references: item.references || "",
                requirement_id: item.requirement_id || "",
                l1: item.l1 || false,
                l2: item.l2 || false,
                l3: item.l3 || false,
                is_web: item.is_web || false,
                is_api: item.is_api || false,
                is_mobile: item.is_mobile || false,
                is_infra: item.is_infra || false,
                is_required: item.is_required ?? true
              }
            }).unwrap();
          } catch (err) {
            console.error(`Failed to update item ${item.item_id}:`, err);
          }
        }

        toast.success("Template updated successfully");
      } else {
        // Create mode: send everything in one request
        const payload = {
          template_name: templateName,
          description,
          version,
          is_active: isActive,
          framework,
          target_types: selectedTargetTypes,
          items: items.map((item, index) => ({
            item_order: index + 1,
            category: item.category || "",
            title: item.title || "",
            description: item.description || "",
            test_objective: item.test_objective || "",
            expected_evidence: item.expected_evidence || "",
            references: item.references || "",
            requirement_id: item.requirement_id || "",
            l1: item.l1 || false,
            l2: item.l2 || false,
            l3: item.l3 || false,
            is_web: item.is_web || false,
            is_api: item.is_api || false,
            is_mobile: item.is_mobile || false,
            is_infra: item.is_infra || false,
            is_required: item.is_required ?? true
          }))
        };
        await createTemplate(payload).unwrap();
        toast.success("Template created successfully");
      }
      navigate("/dashboard/checklist-templates");
    } catch (err: any) {
      toast.error(err?.data?.message || "Failed to save template");
      console.error("Error saving template:", err);
    }
  };

  if (isLoading) {
    return (
      <div className="montserrat-font flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="text-center">
          <div className="mx-auto mb-3 h-12 w-12 animate-spin rounded-full border-4 border-blue-600 border-t-transparent"></div>
          <p className="text-base font-bold text-gray-900">Loading Template...</p>
          <p className="mt-1 text-xs text-gray-600">Please wait</p>
        </div>
      </div>
    );
  }

  return (
    <div className="montserrat-font min-h-screen bg-gray-50">
      {/* Back Button Only */}
      <div className="mx-auto max-w-[1600px] px-4 pt-4">
        <button
          onClick={() => navigate("/dashboard/checklist-templates")}
          className="flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold text-gray-600 transition-all hover:bg-white hover:shadow-sm"
        >
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back
        </button>
      </div>

      <div className="mx-auto max-w-[1600px] px-4 py-4">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Two Column Layout - Basic Info and Target Types */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Basic Information Card */}
            <div className="rounded-2xl bg-white p-6 shadow-sm">
              {/* Header */}
              <div className="flex items-center gap-3 mb-6">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl" style={{ backgroundColor: '#0F172A' }}>
                  <svg className="h-5 w-5" style={{ color: '#58CC02' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h2 className="text-lg font-bold text-gray-900">Basic Information</h2>
              </div>

              {/* Form Fields */}
              <div className="space-y-5">
                {/* Template Name */}
                <div>
                  <label className="mb-2 block text-sm font-semibold text-gray-900">
                    Template Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={templateName}
                    onChange={e => setTemplateName(e.target.value)}
                    className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm transition-all focus:border-gray-300 focus:outline-none focus:ring-0 disabled:bg-gray-50 disabled:text-gray-500"
                    placeholder="e.g., OWASP Web Security Testing Guide v4.2"
                    required
                    disabled={isViewMode}
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="mb-2 block text-sm font-semibold text-gray-900">Description</label>
                  <textarea
                    value={description}
                    onChange={e => setDescription(e.target.value)}
                    rows={4}
                    className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm transition-all focus:border-gray-300 focus:outline-none focus:ring-0 disabled:bg-gray-50 disabled:text-gray-500 resize-none"
                    placeholder="Brief description of this template..."
                    disabled={isViewMode}
                  />
                </div>

                {/* Version, Framework and Status Row */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="mb-2 block text-sm font-semibold text-gray-900">Version</label>
                    <input
                      type="text"
                      value={version}
                      onChange={e => setVersion(e.target.value)}
                      className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm transition-all focus:border-gray-300 focus:outline-none focus:ring-0 disabled:bg-gray-50"
                      placeholder="e.g., 1.0"
                      disabled={isViewMode}
                    />
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-semibold text-gray-900">Framework</label>
                    <select
                      value={framework}
                      onChange={e => setFramework(e.target.value)}
                      className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm transition-all focus:border-gray-300 focus:outline-none focus:ring-0 disabled:bg-gray-50 appearance-none bg-white font-medium"
                      disabled={isViewMode}
                    >
                      <option value="ASVS">ASVS 5.0</option>
                      <option value="OWASP">OWASP (WSTG v4.2)</option>
                      <option value="OWASP_TOP_10">OWASP Top 10 2021</option>
                      <option value="OWASP_API">OWASP API Top 10 2023</option>
                      <option value="CTB">CTB Custom Library</option>
                      <option value="CUSTOM">Custom</option>
                    </select>
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-semibold text-gray-900">Status</label>
                    <button
                      type="button"
                      onClick={() => !isViewMode && setIsActive(!isActive)}
                      className={`w-full rounded-xl border px-4 py-3 text-sm font-medium text-left flex items-center gap-2 transition-all ${
                        isActive
                          ? 'border-green-200 bg-green-50'
                          : 'border-gray-200 bg-gray-50'
                      } ${isViewMode ? 'cursor-default' : 'cursor-pointer hover:border-gray-300'}`}
                      disabled={isViewMode}
                    >
                      <span className={`w-2 h-2 rounded-full ${isActive ? 'bg-green-500' : 'bg-gray-400'}`}></span>
                      <span className={isActive ? 'text-green-700' : 'text-gray-600'}>
                        {isActive ? 'Active' : 'Inactive'}
                      </span>
                    </button>
                  </div>
                </div>

                {/* Action Buttons */}
                {!isViewMode && (
                  <div className="flex items-center gap-3 pt-4">
                    <button
                      type="button"
                      onClick={() => navigate("/dashboard/checklist-templates")}
                      className="flex-1 flex items-center justify-center gap-2 rounded-lg bg-slate-50 border border-slate-100 px-6 py-2.5 text-sm font-medium text-slate-600 transition-all hover:bg-slate-100"
                    >
                      <FiX className="w-4 h-4" />
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isCreating || isUpdating}
                      className="flex-1 flex items-center justify-center gap-2 rounded-lg bg-[#010D2C] px-6 py-2.5 text-sm font-medium text-white transition-all hover:opacity-95 disabled:opacity-50"
                    >
                      <FiCheck className="w-4 h-4" />
                      Submit
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Target Types Card */}
            <div className="rounded-2xl bg-white p-6 shadow-sm">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl" style={{ backgroundColor: '#0F172A' }}>
                    <svg className="h-5 w-5" style={{ color: '#58CC02' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h2 className="text-lg font-bold text-gray-900">
                    Target Types <span className="text-red-500">*</span>
                  </h2>
                </div>
                {!isViewMode && (
                  <button
                    type="submit"
                    disabled={isCreating || isUpdating}
                    className="rounded-lg bg-[#010D2C] px-8 py-2 text-sm font-medium text-[#58CC02] transition-all hover:opacity-95 disabled:opacity-50"
                  >
                    Submit
                  </button>
                )}
              </div>

              {/* Target Type Buttons Grid */}
              <div className="grid grid-cols-2 gap-3">
                {TARGET_TYPES.map(targetType => {
                  const isSelected = selectedTargetTypes.includes(targetType);
                  return (
                    <button
                      key={targetType}
                      type="button"
                      onClick={() => handleTargetTypeToggle(targetType)}
                      disabled={isViewMode}
                      className={`relative flex items-center justify-center rounded-lg px-4 py-3 text-sm font-medium transition-all border ${
                        isSelected
                          ? 'border-[#58CC02] bg-[#f0fdf4] text-[#58CC02]'
                          : 'border-gray-100/80 bg-white text-slate-300 hover:border-slate-200 hover:bg-slate-50'
                      } ${isViewMode ? 'cursor-default' : 'cursor-pointer'}`}
                    >
                      {targetType}
                      {isSelected && (
                        <span className="absolute -top-1.5 -right-1.5 flex h-4.5 w-4.5 items-center justify-center rounded-full bg-[#010D2C] border-2 border-white shadow-sm overflow-hidden p-0.5">
                          <FiCheck className="h-3 w-3 text-white" />
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Checklist Items Filter Bar */}
          <div className="rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4 px-6 py-5 bg-[#0F172A]">
              {/* Left - Title and Count */}
              <div>
                <h2 className="text-base font-bold text-white">Checklist Items</h2>
                <p className="text-sm text-gray-400">
                  {filteredItems.length} of {items.length} items shown
                </p>
              </div>

              {/* Right - Global Actions */}
              <div className="flex items-center gap-3">
                {!isViewMode && (
                  <>
                    <button
                      type="button"
                      onClick={() => setShowImportModal(true)}
                      className="flex items-center gap-2 rounded-xl border border-white/20 bg-white/5 px-4 py-2.5 text-sm font-medium text-white transition-all hover:bg-white/10"
                    >
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                      </svg>
                      Import
                    </button>
                    <button
                      type="button"
                      onClick={handleAddItem}
                      className="flex items-center gap-2 rounded-xl px-5 py-2.5 text-sm font-medium text-white transition-all hover:opacity-90 shadow-lg shadow-[#58CC02]/20"
                      style={{ backgroundColor: '#58CC02' }}
                    >
                      <FiPlus className="h-4 w-4" />
                      Add Item
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* Filter Controls Row */}
            <div className="bg-white px-6 py-4 border-t border-gray-100 flex items-center justify-between gap-4 overflow-x-auto no-scrollbar">
              {/* Search */}
              <div className="relative min-w-[280px] max-w-[400px] flex-shrink-0">
                <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  value={itemSearchQuery}
                  onChange={e => setItemSearchQuery(e.target.value)}
                  placeholder="Search by title, description or ID..."
                  className="w-full rounded-xl border border-gray-200 bg-gray-50 py-2.5 pl-11 pr-4 text-xs transition-all focus:border-blue-400 focus:bg-white focus:outline-none focus:ring-0"
                />
              </div>

              <div className="flex items-center gap-6 flex-shrink-0">
                {/* Category Filter */}
                <div className="flex items-center gap-2">
                  <span className="text-[9px] font-semibold text-gray-400 uppercase tracking-widest flex-shrink-0">Category</span>
                  <select
                    value={filterCategory}
                    onChange={(e) => setFilterCategory(e.target.value)}
                    className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-xs font-medium focus:outline-none focus:border-blue-400 min-w-[140px]"
                  >
                    <option value="All">All Categories</option>
                    {uniqueCategories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>

                {/* Target Mode Toggle */}
                <div className="flex items-center gap-2">
                  <span className="text-[9px] font-semibold text-gray-400 uppercase tracking-widest flex-shrink-0">Platform</span>
                  <div className="flex bg-gray-100 p-1 rounded-xl gap-0.5">
                    {['All', 'Web', 'API', 'Mobile', 'Infra'].map((t) => (
                      <button
                        key={t}
                        type="button"
                        onClick={() => setFilterTarget(t)}
                        className={`px-3 py-1.5 text-[9px] font-semibold uppercase rounded-lg transition-all ${
                          filterTarget === t 
                            ? 'bg-[#58CC02] text-white shadow-sm' 
                            : 'text-gray-400 hover:text-gray-600'
                        }`}
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                </div>

                {/* ASVS Level Filter (if applicable) */}
                {(framework === "ASVS" || framework === "ASVS_5_0") && (
                  <div className="flex items-center gap-2">
                    <span className="text-[9px] font-semibold text-gray-400 uppercase tracking-widest flex-shrink-0">ASVS Level</span>
                    <div className="flex bg-gray-100 p-1 rounded-xl gap-0.5">
                      {['All', 'L1', 'L2', 'L3'].map((l) => (
                        <button
                          key={l}
                          type="button"
                          onClick={() => setFilterAsvsLevel(l)}
                          className={`px-3 py-1.5 text-[9px] font-semibold uppercase rounded-lg transition-all ${
                            filterAsvsLevel === l 
                              ? 'bg-[#58CC02] text-white shadow-sm' 
                              : 'text-gray-400 hover:text-gray-600'
                          }`}
                        >
                          {l}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Checklist Items Table */}
          <div className="rounded-2xl bg-white shadow-sm overflow-hidden">
            {/* Table Header */}
            <div className="grid grid-cols-12 gap-4 px-6 py-4 bg-slate-50 border-b border-gray-100">
              <div className="col-span-5 text-sm font-semibold text-gray-700">Test Item</div>
              <div className="col-span-3 text-sm font-semibold text-gray-700">Category</div>
              <div className="col-span-4 text-sm font-semibold text-gray-700">Req ID & Levels</div>
            </div>

            {/* Table Body */}
            {filteredItems.length === 0 ? (
              <div className="py-16 text-center">
                <svg className="mx-auto mb-3 h-12 w-12 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <p className="text-base font-semibold text-gray-600">No items match your filters</p>
                {!isViewMode && items.length === 0 && <p className="text-sm text-gray-400 mt-1">Click "Add an item" to create your first test item</p>}
              </div>
            ) : (
              <div>
                {filteredItems.map((item, filteredIndex) => (
                  <ChecklistItemRow
                    key={item.tempId || item.item_id}
                    item={item}
                    index={items.indexOf(item)}
                    displayIndex={filteredIndex}
                    onChange={handleItemChange}
                    onRemove={handleRemoveItem}
                    isViewMode={isViewMode}
                    framework={framework}
                  />
                ))}
              </div>
            )}


          </div>
        </form>
      </div>

      {/* Import Modal */}
      {showImportModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="mx-4 w-full max-w-3xl rounded-xl bg-white shadow-2xl">
            <div className="border-b border-gray-200 px-6 py-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-bold text-gray-900">Import Test Cases</h3>
                <button
                  onClick={() => {
                    setShowImportModal(false);
                    setSelectedFile(null);
                    setImportErrors([]);
                  }}
                  className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                >
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="p-6 overflow-y-auto max-h-[75vh]">
              <div className="mb-6">
                <label className="mb-2 block text-sm font-bold text-gray-700">
                  Select File (JSON or CSV)
                </label>
                <input
                  type="file"
                  accept=".json,.csv"
                  onChange={handleFileSelect}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Maximum file size: 5MB. Supported formats: JSON, CSV
                </p>
              </div>

               {/* Sample Files */}
              <div className="mb-6 rounded-lg bg-blue-50 p-4">
                <h4 className="mb-2 text-sm font-bold text-blue-900">Download Sample Files</h4>
                <div className="flex gap-2">
                  <button
                    onClick={() => downloadSampleFile('json')}
                    className="rounded-md bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-700"
                  >
                    Sample JSON
                  </button>
                  <button
                    onClick={() => downloadSampleFile('csv')}
                    className="rounded-md bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-700"
                  >
                    Sample CSV
                  </button>
                  <a
                    href="/sample-files/README.md"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="rounded-md bg-gray-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-gray-700"
                  >
                    Import Guide
                  </a>
                </div>
              </div>

              {/* Methodology Import Guidelines */}
              <div className="mb-6 rounded-lg border border-slate-100 bg-slate-50/50 p-5">
                <h4 className="mb-4 text-xs font-bold uppercase tracking-wider text-[#010D2C] border-b border-slate-200 pb-2">CSV / JSON Column Mapping Guidelines</h4>
                
                {/* Column Requirements Table */}
                <div className="overflow-x-auto mb-5 rounded-lg border border-slate-200 bg-white">
                  <table className="w-full text-left text-xs border-collapse">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-200 text-slate-400 font-bold uppercase text-[10px]">
                        <th className="p-3">Column Name</th>
                        <th className="p-3">Type</th>
                        <th className="p-3">Requirement</th>
                        <th className="p-3">Default Value / Details</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 text-slate-700">
                      <tr>
                        <td className="p-3"><code className="font-mono bg-red-50 text-red-600 px-1 py-0.5 rounded font-bold">title</code></td>
                        <td className="p-3 font-semibold text-slate-500">String</td>
                        <td className="p-3"><span className="text-red-500 font-bold">Required</span></td>
                        <td className="p-3 text-slate-500">The primary title/name of the test case.</td>
                      </tr>
                      <tr>
                        <td className="p-3"><code className="font-mono text-slate-800 bg-slate-100 px-1 py-0.5 rounded">category</code></td>
                        <td className="p-3 font-semibold text-slate-500">String</td>
                        <td className="p-3 text-slate-400">Optional</td>
                        <td className="p-3 text-slate-500">The section heading (e.g. Authentication).</td>
                      </tr>
                      <tr>
                        <td className="p-3"><code className="font-mono text-slate-800 bg-slate-100 px-1 py-0.5 rounded">description</code></td>
                        <td className="p-3 font-semibold text-slate-500">Text</td>
                        <td className="p-3 text-slate-400">Optional</td>
                        <td className="p-3 text-slate-500">Detailed security control instruction.</td>
                      </tr>
                      <tr>
                        <td className="p-3"><code className="font-mono text-slate-800 bg-slate-100 px-1 py-0.5 rounded">test_objective</code></td>
                        <td className="p-3 font-semibold text-slate-500">Text</td>
                        <td className="p-3 text-slate-400">Optional</td>
                        <td className="p-3 text-slate-500">Objective or validation procedure.</td>
                      </tr>
                      <tr>
                        <td className="p-3"><code className="font-mono text-slate-800 bg-slate-100 px-1 py-0.5 rounded">expected_evidence</code></td>
                        <td className="p-3 font-semibold text-slate-500">Text</td>
                        <td className="p-3 text-slate-400">Optional</td>
                        <td className="p-3 text-slate-500">Expected verification evidence.</td>
                      </tr>
                      <tr>
                        <td className="p-3"><code className="font-mono text-slate-800 bg-slate-100 px-1 py-0.5 rounded">references</code></td>
                        <td className="p-3 font-semibold text-slate-500">Text</td>
                        <td className="p-3 text-slate-400">Optional</td>
                        <td className="p-3 text-slate-500">External security links (OWASP, WSTG).</td>
                      </tr>
                      <tr>
                        <td className="p-3"><code className="font-mono text-slate-800 bg-slate-100 px-1 py-0.5 rounded">requirement_id</code></td>
                        <td className="p-3 font-semibold text-slate-500">String</td>
                        <td className="p-3 text-slate-400">Optional</td>
                        <td className="p-3 text-slate-500">ASVS/OWASP framework reference ID.</td>
                      </tr>
                      <tr className="bg-slate-50/50">
                        <td className="p-3"><code className="font-mono text-slate-800 bg-slate-100 px-1 py-0.5 rounded">l1</code>, <code className="font-mono text-slate-800 bg-slate-100 px-1 py-0.5 rounded">l2</code>, <code className="font-mono text-slate-800 bg-slate-100 px-1 py-0.5 rounded">l3</code></td>
                        <td className="p-3 font-semibold text-slate-500">Boolean</td>
                        <td className="p-3 text-slate-400"><strong className="text-slate-500">Not Required</strong></td>
                        <td className="p-3 text-slate-500">ASVS Levels (defaults to <code className="font-mono text-[10px]">false</code>).</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <p className="text-xs text-slate-600 font-semibold mb-3 leading-relaxed">
                  Methodology Scope Flags (maps which targets trigger this item on a program):
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-lg border border-blue-100 bg-blue-50/20 p-2.5">
                    <span className="inline-block rounded bg-blue-500/10 px-1.5 py-0.5 text-[9px] font-bold text-blue-600 uppercase border border-blue-500/20 mb-1">Web</span>
                    <p className="text-[10px] text-slate-600 font-medium">Use column <code className="font-mono text-blue-600">is_web</code> or <code className="font-mono text-blue-600">web</code>. (default: <code className="font-mono">true</code>).</p>
                  </div>
                  <div className="rounded-lg border border-purple-100 bg-purple-50/20 p-2.5">
                    <span className="inline-block rounded bg-purple-500/10 px-1.5 py-0.5 text-[9px] font-bold text-purple-600 uppercase border border-purple-500/20 mb-1">API</span>
                    <p className="text-[10px] text-slate-600 font-medium">Use column <code className="font-mono text-purple-600">is_api</code> or <code className="font-mono text-purple-600">api</code>. (default: <code className="font-mono">true</code>).</p>
                  </div>
                  <div className="rounded-lg border border-emerald-100 bg-emerald-50/20 p-2.5">
                    <span className="inline-block rounded bg-emerald-500/10 px-1.5 py-0.5 text-[9px] font-bold text-emerald-600 uppercase border border-emerald-500/20 mb-1">Mobile</span>
                    <p className="text-[10px] text-slate-600 font-medium">Use column <code className="font-mono text-emerald-600">is_mobile</code> or <code className="font-mono text-emerald-600">mobile</code>. (default: <code className="font-mono">false</code>).</p>
                  </div>
                  <div className="rounded-lg border border-orange-100 bg-orange-50/20 p-2.5">
                    <span className="inline-block rounded bg-orange-500/10 px-1.5 py-0.5 text-[9px] font-bold text-orange-600 uppercase border border-orange-500/20 mb-1">Infrastructure</span>
                    <p className="text-[10px] text-slate-600 font-medium">Use column <code className="font-mono text-orange-600">is_infra</code>, <code className="font-mono text-orange-600">infra</code>, or <code className="font-mono text-orange-600">infrastructure</code>. (default: <code className="font-mono">false</code>).</p>
                  </div>
                </div>
              </div>

              {/* Single-Methodology Configuration Guide */}
              <div className="mb-6 rounded-lg border border-slate-200 bg-slate-50/30 p-5">
                <h4 className="mb-3 text-xs font-bold uppercase tracking-wider text-[#010D2C] border-b border-slate-200 pb-2 flex items-center gap-2">
                  <FiInfo className="text-blue-500 w-4 h-4" /> 
                  Single-Methodology Setup Guide (CSV & JSON)
                </h4>
                <p className="text-xs text-slate-600 mb-4 leading-relaxed">
                  To import checklist items exclusive to a single methodology (ensuring zero mix-ups), map the target scope columns in your files as shown below:
                </p>
                
                <div className="space-y-4">
                  {/* Web Only */}
                  <div className="rounded-xl border border-blue-100 bg-white p-4 shadow-sm">
                    <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                      <span className="rounded bg-blue-500/10 px-2 py-0.5 text-xs font-bold text-blue-600 uppercase border border-blue-500/20">Web Only</span>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => downloadSampleFile('web-csv')}
                          className="rounded bg-blue-600 hover:bg-blue-700 text-[10px] font-bold text-white px-2 py-1 transition-all"
                        >
                          Download CSV
                        </button>
                        <button
                          type="button"
                          onClick={() => downloadSampleFile('web-json')}
                          className="rounded bg-blue-600 hover:bg-blue-700 text-[10px] font-bold text-white px-2 py-1 transition-all"
                        >
                          Download JSON
                        </button>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
                      <div>
                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1">CSV Columns</span>
                        <pre className="font-mono text-[10px] bg-slate-900 text-slate-100 p-2 rounded overflow-x-auto">
                          is_web,is_api,is_mobile,is_infra{"\n"}
                          true,false,false,false
                        </pre>
                      </div>
                      <div>
                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1">JSON Fields</span>
                        <pre className="font-mono text-[10px] bg-slate-900 text-slate-100 p-2 rounded overflow-x-auto">
                          {"{"}{"\n"}
                          {"  "}"is_web": true,{"\n"}
                          {"  "}"is_api": false,{"\n"}
                          {"  "}"is_mobile": false,{"\n"}
                          {"  "}"is_infra": false{"\n"}
                          {"}"}
                        </pre>
                      </div>
                    </div>
                  </div>

                  {/* API Only */}
                  <div className="rounded-xl border border-purple-100 bg-white p-4 shadow-sm">
                    <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                      <span className="rounded bg-purple-500/10 px-2 py-0.5 text-xs font-bold text-purple-600 uppercase border border-purple-500/20">API Only</span>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => downloadSampleFile('api-csv')}
                          className="rounded bg-purple-600 hover:bg-purple-700 text-[10px] font-bold text-white px-2 py-1 transition-all"
                        >
                          Download CSV
                        </button>
                        <button
                          type="button"
                          onClick={() => downloadSampleFile('api-json')}
                          className="rounded bg-purple-600 hover:bg-purple-700 text-[10px] font-bold text-white px-2 py-1 transition-all"
                        >
                          Download JSON
                        </button>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
                      <div>
                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1">CSV Columns</span>
                        <pre className="font-mono text-[10px] bg-slate-900 text-slate-100 p-2 rounded overflow-x-auto">
                          is_web,is_api,is_mobile,is_infra{"\n"}
                          false,true,false,false
                        </pre>
                      </div>
                      <div>
                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1">JSON Fields</span>
                        <pre className="font-mono text-[10px] bg-slate-900 text-slate-100 p-2 rounded overflow-x-auto">
                          {"{"}{"\n"}
                          {"  "}"is_web": false,{"\n"}
                          {"  "}"is_api": true,{"\n"}
                          {"  "}"is_mobile": false,{"\n"}
                          {"  "}"is_infra": false{"\n"}
                          {"}"}
                        </pre>
                      </div>
                    </div>
                  </div>

                  {/* Mobile Only */}
                  <div className="rounded-xl border border-emerald-100 bg-white p-4 shadow-sm">
                    <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                      <div>
                        <span className="rounded bg-emerald-500/10 px-2 py-0.5 text-xs font-bold text-emerald-600 uppercase border border-emerald-500/20 mr-2">Mobile Only</span>
                        <span className="text-[10px] font-semibold text-slate-400 italic">(L1/L2/L3 columns omitted)</span>
                      </div>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => downloadSampleFile('mobile-only-csv')}
                          className="rounded bg-[#58CC02] hover:opacity-90 text-[10px] font-bold text-white px-2 py-1 transition-all"
                        >
                          Download CSV
                        </button>
                        <button
                          type="button"
                          onClick={() => downloadSampleFile('mobile-only-json')}
                          className="rounded bg-[#58CC02] hover:opacity-90 text-[10px] font-bold text-white px-2 py-1 transition-all"
                        >
                          Download JSON
                        </button>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
                      <div>
                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1">CSV Columns (No L1/L2/L3 columns)</span>
                        <pre className="font-mono text-[10px] bg-slate-900 text-slate-100 p-2 rounded overflow-x-auto">
                          is_web,is_api,is_mobile,is_infra{"\n"}
                          false,false,true,false
                        </pre>
                      </div>
                      <div>
                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1">JSON Fields (No L1/L2/L3 keys)</span>
                        <pre className="font-mono text-[10px] bg-slate-900 text-slate-100 p-2 rounded overflow-x-auto">
                          {"{"}{"\n"}
                          {"  "}"is_web": false,{"\n"}
                          {"  "}"is_api": false,{"\n"}
                          {"  "}"is_mobile": true,{"\n"}
                          {"  "}"is_infra": false{"\n"}
                          {"}"}
                        </pre>
                      </div>
                    </div>
                  </div>

                  {/* Infra Only */}
                  <div className="rounded-xl border border-orange-100 bg-white p-4 shadow-sm">
                    <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                      <div>
                        <span className="rounded bg-orange-500/10 px-2 py-0.5 text-xs font-bold text-orange-600 uppercase border border-orange-500/20 mr-2">Infra Only</span>
                        <span className="text-[10px] font-semibold text-slate-400 italic">(L1/L2/L3 columns omitted)</span>
                      </div>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => downloadSampleFile('infra-only-csv')}
                          className="rounded bg-orange-600 hover:bg-orange-700 text-[10px] font-bold text-white px-2 py-1 transition-all"
                        >
                          Download CSV
                        </button>
                        <button
                          type="button"
                          onClick={() => downloadSampleFile('infra-only-json')}
                          className="rounded bg-orange-600 hover:bg-orange-700 text-[10px] font-bold text-white px-2 py-1 transition-all"
                        >
                          Download JSON
                        </button>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
                      <div>
                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1">CSV Columns (No L1/L2/L3 columns)</span>
                        <pre className="font-mono text-[10px] bg-slate-900 text-slate-100 p-2 rounded overflow-x-auto">
                          is_web,is_api,is_mobile,is_infra{"\n"}
                          false,false,false,true
                        </pre>
                      </div>
                      <div>
                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1">JSON Fields (No L1/L2/L3 keys)</span>
                        <pre className="font-mono text-[10px] bg-slate-900 text-slate-100 p-2 rounded overflow-x-auto">
                          {"{"}{"\n"}
                          {"  "}"is_web": false,{"\n"}
                          {"  "}"is_api": false,{"\n"}
                          {"  "}"is_mobile": false,{"\n"}
                          {"  "}"is_infra": true{"\n"}
                          {"}"}
                        </pre>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Import Errors */}
              {importErrors.length > 0 && (
                <div className="mb-6 rounded-lg bg-red-50 p-4">
                  <h4 className="mb-2 text-sm font-bold text-red-900">Import Errors</h4>
                  <ul className="space-y-1 text-xs text-red-700">
                    {importErrors.map((error, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <span className="mt-0.5 h-1 w-1 rounded-full bg-red-500"></span>
                        {error}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* File Info */}
              {selectedFile && (
                <div className="mb-6 rounded-lg bg-gray-50 p-4">
                  <h4 className="mb-2 text-sm font-bold text-gray-900">Selected File</h4>
                  <div className="text-xs text-gray-600">
                    <p><strong>Name:</strong> {selectedFile.name}</p>
                    <p><strong>Size:</strong> {(selectedFile.size / 1024).toFixed(1)} KB</p>
                    <p><strong>Type:</strong> {selectedFile.type || 'Unknown'}</p>
                  </div>
                </div>
              )}
            </div>

            <div className="border-t border-gray-200 px-6 py-4">
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowImportModal(false);
                    setSelectedFile(null);
                    setImportErrors([]);
                  }}
                  className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleImportSubmit}
                  disabled={!selectedFile || isImporting}
                  className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {isImporting ? (
                    <>
                      <svg className="mr-2 inline h-4 w-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      Importing...
                    </>
                  ) : (
                    'Import Test Cases'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Checklist Item Row Component - Table Style
interface ChecklistItemRowProps {
  item: ChecklistItemForm;
  index: number;
  displayIndex: number;
  onChange: (index: number, field: keyof ChecklistItemForm, value: any) => void;
  onRemove: (index: number) => void;
  isViewMode: boolean;
  framework: string;
}

const ChecklistItemRow: React.FC<ChecklistItemRowProps> = ({
  item,
  index,
  displayIndex,
  onChange,
  onRemove,
  isViewMode,
  framework
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="border-b border-gray-100 last:border-b-0">
      {/* Row */}
      <div className="grid grid-cols-12 gap-4 px-6 py-4 items-start hover:bg-gray-50 transition-colors">
        {/* Test Item - Index + Title */}
        <div className="col-span-5 flex items-start gap-4">
          <span className="text-sm text-gray-400 font-bold w-6 pt-0.5 font-mono">
            {String(displayIndex + 1).padStart(2, '0')}
          </span>
          <div className="flex-1 min-w-0 group/title">
            {isViewMode ? (
              <div className="text-sm font-semibold text-gray-900 break-words leading-relaxed pt-0.5 group-hover/title:text-blue-600 transition-colors">
                {item.title || <span className="text-gray-300 italic">No title defined</span>}
              </div>
            ) : (
              <textarea
                value={item.title || ""}
                onChange={(e) => onChange(index, "title", e.target.value)}
                placeholder="Enter test item title..."
                rows={2}
                className="w-full border-0 bg-transparent p-0 text-sm font-semibold text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-0 disabled:opacity-60 resize-none leading-relaxed transition-all focus:bg-blue-50/50 rounded"
              />
            )}
          </div>
        </div>

        {/* Category Badge */}
        <div className="col-span-3">
          <input
            type="text"
            value={item.category || ""}
            onChange={(e) => onChange(index, "category", e.target.value)}
            placeholder="Category"
            className="inline-block rounded-lg border border-blue-200 bg-blue-50 px-3 py-1.5 text-xs font-medium text-blue-700 focus:outline-none focus:border-blue-400 disabled:opacity-60 w-auto max-w-[180px]"
            disabled={isViewMode}
          />
        </div>

        {/* Req ID & Levels + Actions */}
        <div className="col-span-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <input
              type="text"
              value={item.requirement_id || ""}
              onChange={(e) => onChange(index, "requirement_id", e.target.value)}
              placeholder="ID"
              className="w-16 rounded border border-gray-200 px-2 py-1 text-xs font-mono focus:outline-none focus:border-blue-400 disabled:opacity-60"
              disabled={isViewMode}
            />
            {/* Target Badges (Web/API/Mobile/Infra) */}
            <div className="flex gap-1 flex-wrap max-w-[120px]">
              {item.is_web && (
                <span className="inline-flex items-center rounded bg-blue-500/10 px-1.5 py-0.5 text-[8px] font-bold text-blue-600 uppercase border border-blue-500/20">
                  Web
                </span>
              )}
              {item.is_api && (
                <span className="inline-flex items-center rounded bg-purple-500/10 px-1.5 py-0.5 text-[8px] font-bold text-purple-600 uppercase border border-purple-500/20">
                  API
                </span>
              )}
              {item.is_mobile && (
                <span className="inline-flex items-center rounded bg-emerald-500/10 px-1.5 py-0.5 text-[8px] font-bold text-emerald-600 uppercase border border-emerald-500/20">
                  Mobile
                </span>
              )}
              {item.is_infra && (
                <span className="inline-flex items-center rounded bg-orange-500/10 px-1.5 py-0.5 text-[8px] font-bold text-orange-600 uppercase border border-orange-500/20">
                  Infra
                </span>
              )}
            </div>
            {(framework === "ASVS" || framework === "ASVS_5_0") && (
              <div className="flex items-center gap-1">
                {ASVS_LEVELS.map((level) => {
                  const levelKey = level.toLowerCase() as "l1" | "l2" | "l3";
                  const isActive = item[levelKey];
                  return (
                    <button
                      key={level}
                      type="button"
                      onClick={() => onChange(index, levelKey, !isActive)}
                      disabled={isViewMode}
                      className={`h-5 w-6 rounded text-[10px] font-bold transition-all ${
                        isActive ? "bg-[#010D2C] text-white" : "bg-gray-100 text-gray-400 hover:bg-gray-200"
                      } ${isViewMode ? "cursor-default" : "cursor-pointer"}`}
                    >
                      {level}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setIsExpanded(!isExpanded)}
              className="p-1.5 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className={`h-4 w-4 transition-transform ${isExpanded ? "rotate-180" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {!isViewMode && (
              <button
                type="button"
                onClick={() => onRemove(index)}
                className="p-1.5 text-orange-400 hover:text-orange-600 transition-colors"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="mx-6 mb-4 rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
          {/* Header Row */}
          <div className="flex flex-wrap items-start justify-between gap-6 mb-8 pb-6 border-b border-gray-100">
            <div className="flex-1 min-w-[300px]">
              <label className="mb-2 block text-xs font-medium text-gray-400">Section / Category</label>
              <input
                type="text"
                value={item.category || ""}
                onChange={(e) => onChange(index, "category", e.target.value)}
                placeholder="e.g., V1 Architecture"
                className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-0 disabled:bg-gray-100"
                disabled={isViewMode}
              />
            </div>
            
            <div className="flex gap-8 flex-wrap">
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => onChange(index, "is_web", !item.is_web)}
                  disabled={isViewMode}
                  className={`relative inline-flex h-6 w-10 items-center rounded-full transition-colors ${
                    item.is_web ? "bg-blue-500" : "bg-gray-300"
                  } ${isViewMode ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${item.is_web ? "translate-x-5" : "translate-x-1"}`} />
                </button>
                <span className="text-xs font-bold text-blue-500 uppercase tracking-wider">Web</span>
              </div>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => onChange(index, "is_api", !item.is_api)}
                  disabled={isViewMode}
                  className={`relative inline-flex h-6 w-10 items-center rounded-full transition-colors ${
                    item.is_api ? "bg-purple-500" : "bg-gray-300"
                  } ${isViewMode ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${item.is_api ? "translate-x-5" : "translate-x-1"}`} />
                </button>
                <span className="text-xs font-bold text-purple-500 uppercase tracking-wider">API</span>
              </div>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => onChange(index, "is_mobile", !item.is_mobile)}
                  disabled={isViewMode}
                  className={`relative inline-flex h-6 w-10 items-center rounded-full transition-colors ${
                    item.is_mobile ? "bg-emerald-500" : "bg-gray-300"
                  } ${isViewMode ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${item.is_mobile ? "translate-x-5" : "translate-x-1"}`} />
                </button>
                <span className="text-xs font-bold text-emerald-600 uppercase tracking-wider">Mobile</span>
              </div>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => onChange(index, "is_infra", !item.is_infra)}
                  disabled={isViewMode}
                  className={`relative inline-flex h-6 w-10 items-center rounded-full transition-colors ${
                    item.is_infra ? "bg-orange-500" : "bg-gray-300"
                  } ${isViewMode ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${item.is_infra ? "translate-x-5" : "translate-x-1"}`} />
                </button>
                <span className="text-xs font-bold text-orange-600 uppercase tracking-wider">Infra</span>
              </div>
            </div>

            <div className="flex items-center gap-3 pt-6">
              <button
                type="button"
                onClick={() => onChange(index, "is_required", !(item.is_required ?? true))}
                disabled={isViewMode}
                className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${
                  (item.is_required ?? true) ? "bg-blue-500" : "bg-gray-300"
                } ${isViewMode ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
              >
                <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-sm transition-transform ${(item.is_required ?? true) ? "translate-x-6" : "translate-x-1"}`} />
              </button>
              <span className="text-sm font-medium text-blue-500">Required</span>
            </div>
          </div>

          <div className="space-y-6">
            <div>
              <label className="mb-2 flex items-center gap-2 text-xs font-medium text-gray-400">
                <FiLayers className="h-4 w-4" /> Description
              </label>
              <textarea
                value={item.description || ""}
                onChange={(e) => onChange(index, "description", e.target.value)}
                placeholder="Detailed description..."
                rows={3}
                className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-0 disabled:bg-gray-100"
                disabled={isViewMode}
              />
            </div>
            <div>
              <label className="mb-2 flex items-center gap-2 text-xs font-medium text-gray-400">
                <FiTarget className="h-4 w-4" /> Test Objective
              </label>
              <textarea
                value={item.test_objective || ""}
                onChange={(e) => onChange(index, "test_objective", e.target.value)}
                placeholder="How to test..."
                rows={2}
                className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-0 disabled:bg-gray-100"
                disabled={isViewMode}
              />
            </div>
            <div>
              <label className="mb-2 flex items-center gap-2 text-xs font-medium text-gray-400">
                <FiZap className="h-4 w-4" /> Expected Evidence
              </label>
              <textarea
                value={item.expected_evidence || ""}
                onChange={(e) => onChange(index, "expected_evidence", e.target.value)}
                placeholder="What evidence to provide..."
                rows={2}
                className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-0 disabled:bg-gray-100"
                disabled={isViewMode}
              />
            </div>
            <div>
              <label className="mb-2 flex items-center gap-2 text-xs font-medium text-gray-400">
                <FiInfo className="h-4 w-4" /> References
              </label>
              <input
                type="text"
                value={item.references || ""}
                onChange={(e) => onChange(index, "references", e.target.value)}
                placeholder="e.g., OWASP A1, ASVS V1.1"
                className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-0 disabled:bg-gray-100"
                disabled={isViewMode}
              />
            </div>
          </div>

          {!isViewMode && (
            <div className="flex justify-end mt-8">
              <button
                type="button"
                onClick={() => setIsExpanded(false)}
                className="flex items-center gap-2 rounded-xl px-8 py-3 text-sm font-medium text-white transition-all hover:opacity-90 active:scale-95 shadow-md shadow-[#0F172A]/20"
                style={{ backgroundColor: "#0F172A" }}
              >
                <FiCheck className="h-4 w-4" />
                Done
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ChecklistTemplateEditor;